import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ComposedChart, Bar, Line, LineChart, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid,
} from "recharts";
import { apiGet } from "../api";
import usePageTitle from "../hooks/usePageTitle";
import { PageHeader, KpiRow, Card, SectionHeading, DataTable } from "../components/dc";

const VIOLET = "#a855f7", PINK = "#ec4899", CYAN = "#67e0f0", GREEN = "#34d399", AMBER = "#fbbf24", RED = "#fb7185", MUTE = "#9d8fc0";
const AXIS = { fontSize: 11, fill: MUTE };
const ttStyle = { background: "#17102b", border: "1px solid #2a1c47", borderRadius: 8, color: "#f5f2fb", fontSize: 12 };
const gbp0 = (n) => "£" + Math.round(n || 0).toLocaleString("en-GB");
const gbp2 = (n) => "£" + Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const signPct = (n) => (n == null ? "—" : `${n >= 0 ? "+" : ""}${Number(n).toFixed(1)}%`);
const SS_ALIAS = { CEF: "City Electric Supply", CPS: "City Plumbing Supplies", WOLSELEY: "Wolseley", YESSS: "YESSS Electrical", "TRAVIS PERKINS": "Travis Perkins", UKPS: "UK Plumbing Supplies" };

export default function SupplierProfile() {
  const { name } = useParams();
  const supplier = decodeURIComponent(name || "");
  usePageTitle(`QBR — ${supplier}`);
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [pm, inf, oc, ss, subs] = await Promise.all([
          apiGet("/dashboard/supplier-metrics"),
          apiGet("/dashboard/inflation-index"),
          apiGet("/dashboard/supplier-overcharges", { params: { supplier, limit: 50 } }),
          apiGet("/supply-scanner/stats"),
          apiGet("/portal/admin/submissions", { params: { status: "all", limit: 200 } }),
        ]);
        const up = supplier.toUpperCase();
        const row = (pm?.metrics || []).find((m) => (m.supplier || "").toUpperCase() === up) || {};
        const byMonth = (pm?.metrics_by_month || []).filter((m) => (m.supplier || "").toUpperCase() === up)
          .sort((a, b) => (a.month > b.month ? 1 : -1));
        const infSummary = (inf?.summary || []).find((s) => (s.supplier || "").toUpperCase() === up) || {};
        const priceSeries = (inf?.series || [])
          .filter((r) => r[supplier] != null)
          .map((r) => ({ month: r.month, supplier: r[supplier], market: r["All suppliers"] }));
        const ssName = (SS_ALIAS[up] || supplier).toUpperCase();
        const cov = (ss?.coverage || []).find((c) => (c.supplier || "").toUpperCase() === ssName);
        const spend = ((ss?.supplierChart || []).find((c) => (c.label || "").toUpperCase() === ssName) || {}).spend;
        const submissions = (subs?.submissions || []).filter((s) => (s.supplier_name || "").toUpperCase() === up);
        setD({ row, byMonth, infSummary, priceSeries, cov, ssSpend: spend, submissions, ocItems: oc?.items || [], ocSummary: oc?.summary || {} });
      } catch { setD({ error: true }); }
      finally { setLoading(false); }
    })();
  }, [supplier]);

  const back = <Link to="/qbr/scorecards" style={{ color: VIOLET, fontSize: 13, textDecoration: "none" }}>← Scorecards</Link>;

  if (loading) return (<div data-testid="qbr-supplier"><PageHeader title={supplier} subtitle="Supplier profile" right={back} /><div style={{ color: MUTE, padding: 40 }}>Loading…</div></div>);
  if (!d || d.error) return (<div data-testid="qbr-supplier"><PageHeader title={supplier} subtitle="Supplier profile" right={back} /><Card><div style={{ color: MUTE }}>Couldn't load this supplier.</div></Card></div>);

  const row = d.row;
  const coverage = row.price_file_coverage_pct || 0;
  const ragColor = coverage >= 90 ? GREEN : coverage >= 75 ? AMBER : RED;

  return (
    <div data-testid="qbr-supplier">
      <PageHeader title={supplier} subtitle="Supplier profile — pricing, coverage, overcharge and submissions" right={back} />

      <KpiRow items={[
        { label: "Spend (in scope)", value: gbp0(row.total_spend), valueColor: "var(--color-text-1)" },
        { label: "Overcharge", value: gbp0(row.overcharge_value), valueColor: (row.overcharge_value || 0) > 0 ? RED : GREEN },
        { label: "Net after credits", value: gbp0(row.net_overcharge != null ? row.net_overcharge : row.overcharge_value), valueColor: PINK },
        { label: "Credit recovered", value: gbp0(row.credit_received), valueColor: GREEN },
        { label: "Coverage", value: `${(coverage).toFixed(1)}%`, valueColor: ragColor },
        { label: "Price move 3m", value: signPct(d.infSummary.three_m_pct), valueColor: (d.infSummary.three_m_pct || 0) > 0 ? RED : GREEN },
        { label: "YoY", value: signPct(d.infSummary.yoy_pct), valueColor: MUTE },
      ]} />

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
        <Card>
          <SectionHeading title="Spend & overcharge by month" />
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={d.byMonth} margin={{ top: 6, right: 8, left: -8, bottom: 0 }}>
                <CartesianGrid stroke="#241a3d" vertical={false} />
                <XAxis dataKey="month" tick={AXIS} />
                <YAxis yAxisId="l" tick={AXIS} tickFormatter={(v) => `£${Math.round(v / 1000)}k`} />
                <YAxis yAxisId="r" orientation="right" tick={AXIS} tickFormatter={(v) => `£${Math.round(v / 1000)}k`} />
                <Tooltip contentStyle={ttStyle} formatter={(v) => gbp0(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar yAxisId="l" dataKey="total_spend" name="Spend" fill={VIOLET} radius={[4, 4, 0, 0]} />
                <Line yAxisId="r" type="monotone" dataKey="overcharge_value" name="Overcharge" stroke={RED} strokeWidth={2} dot={false} />
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card>
          <SectionHeading title="Price index" subtitle="Rebased to 100 · vs all-supplier composite" />
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={d.priceSeries} margin={{ top: 6, right: 8, left: -12, bottom: 0 }}>
                <CartesianGrid stroke="#241a3d" vertical={false} />
                <XAxis dataKey="month" tick={AXIS} />
                <YAxis tick={AXIS} domain={["auto", "auto"]} />
                <Tooltip contentStyle={ttStyle} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="supplier" name={supplier} stroke={PINK} strokeWidth={2} dot={false} />
                <Line type="monotone" dataKey="market" name="All suppliers" stroke={CYAN} strokeWidth={1.5} strokeDasharray="5 4" dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {d.cov && (
        <Card style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 14, fontSize: 12.5, color: MUTE }}>
            <span style={{ color: CYAN, fontWeight: 600 }}>OSS orders (Supply Scanner)</span>
            <span>{(d.cov.total || 0).toLocaleString()} orders</span>
            <span>{gbp0(d.ssSpend)} spend</span>
            <span>{Math.round(d.cov.completedPct || 0)}% completed</span>
            <span style={{ opacity: 0.7 }}>a supplementary data point — not the supplier's total service.</span>
          </div>
        </Card>
      )}

      <Card style={{ marginBottom: 16 }}>
        <SectionHeading title="Top overcharge lines" subtitle={`${d.ocSummary.total_items || 0} flagged · ${gbp0(d.ocSummary.total_overcharge)} total`} />
        <DataTable perPage={10} searchKeys={["sku", "description"]} searchPlaceholder="Search SKU / description…"
          columns={[
            { key: "sku", label: "SKU", width: "1fr" },
            { key: "description", label: "Description", width: "1.6fr" },
            { key: "invoice_date", label: "Month", align: "right", width: "0.9fr", render: (r) => r.invoice_date || "—" },
            { key: "invoice_price", label: "Invoiced", align: "right", width: "0.9fr", render: (r) => gbp2(r.invoice_price) },
            { key: "price_file_price", label: "Price file", align: "right", width: "0.9fr", render: (r) => gbp2(r.price_file_price) },
            { key: "overcharge_amount", label: "Overcharge", align: "right", width: "1fr", render: (r) => <span style={{ color: RED, fontWeight: 600 }}>{gbp2(r.overcharge_amount)}</span> },
            { key: "classification", label: "Class", width: "1fr", render: (r) => <span style={{ color: r.classification === "True Overcharge" ? RED : AMBER, fontSize: 11.5 }}>{r.classification || "—"}</span> },
          ]}
          rows={d.ocItems} />
      </Card>

      <Card>
        <SectionHeading title="Recent submissions" subtitle="Price updates this supplier has submitted through the portal" />
        {d.submissions.length === 0
          ? <div style={{ color: MUTE, fontSize: 13 }}>No submissions found for this supplier.</div>
          : <DataTable perPage={8}
              columns={[
                { key: "submitted_at", label: "Submitted", width: "1.1fr", render: (r) => r.submitted_at ? new Date(r.submitted_at).toLocaleDateString("en-GB") : "—" },
                { key: "status", label: "Status", width: "1fr" },
                { key: "type", label: "Type", width: "1fr", render: (r) => r.type === "manual" ? "Manual" : "Bulk" },
                { key: "sku_count", label: "Rows", align: "right", width: "0.7fr", render: (r) => r.sku_count ?? r.rows_imported ?? r.row_count ?? r.rows_count ?? "—" },
              ]}
              rows={d.submissions} />}
      </Card>
    </div>
  );
}
